<template>
  <div class="rights">
    <div class="page__header">
      <div class="page__title">{{ $t(`settingsPage.${"userRights"}`) }}</div>
      <div class="buttons">
        <button class="button button--default button--animated" v-if="can('permission_create') || can('role_create')"><div class="button__icon"></div></button>
        <div class="dropdown" v-if="can('permission_create') || can('role_create')">
          <div class="dropdown__container">
            <div
              v-if="can('role_create')"
              class="dropdown__item"
              @click="store.commit('setShowAddRoleModal', true)"
            >
              {{ $t(`addRoleModal.${"addRoleBtn"}`) }}
            </div>
            <div
              v-if="can('permission_create')"
              class="dropdown__item"
              @click="store.commit('setShowAddPermissionModal', true)"
            >
              {{ $t(`addPermissionModal.${"title"}`) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="rights__container">
      <div class="roles box box--default">
        <div class="label label--gradient">{{ $t(`addRoleModal.${"chooseRole"}`) }}</div>
        <Loading v-if="rolesLoading" />
        <div v-else class="roles__body" >

          <div class="user__select">
            <VSelect
              class="select select--default"
              :options="roleTeams"
              :reduce="(team) => team.id" 
              label="name"
              placeholder="Select Team"
              @update:modelValue="onTeamSelected"
              v-model="activeTeamId"
            />
          </div>
   
          <NoData v-if="roleCollectionError?.status === 403" type="no-permission" />
          <div v-else>
            <div
              class="roles__role"
              :class="{ 'roles__role--selected': selectedRole.id === role.id }"
              v-for="role in roles"
              :key="role.id"
              @click="selectRole(role)"
            >
              {{ role.title }} 
            </div>
          </div>
        </div>
      </div>
      <div class="permissions box box--default">
        <Loading v-if="rolesLoading" />

        <template v-else>
          <div v-if="!permissionsLoading && permissionsData.length > 0" class="label label--gradient">{{ selectedRole.title }}</div>
          <div class="page__header delete-role-btn-wrapper" v-if="!permissionsLoading && permissionsData.length > 0"> 
              <div v-if="can('role_delete')" class="icon icon__delete delete-role-btn" @click="deleteReportModal(selectedRole.id)"></div>
          </div>
          <Loading v-if="permissionsLoading" />
          
          <div
             v-if="can('permission_access')"
            class="permissions__body"
            :class="{ 'permissions__body--stretch': !showButtons }"
            ref="permissions"
          > 
         
            <template v-if="!permissionsLoading && permissionsData.length > 0">
              <div
                class="permissions__permission"
                v-for="permission in permissionsData"
                :key="permission.id"
              >
                <span>{{ permission.title }}</span>
                <div class="checkbox" v-if="can('permission_edit')">
                  <input
                    type="checkbox"
                    :id="permission.title + permission.id"
                    class="checkbox__checkbox"
                    :checked="permissionIds.includes(permission.id)"
                    @change="togglePermissions($event, permission.id)"
                  />
                  <label
                    :for="permission.title + permission.id"
                    class="checkbox__label"
                  ></label>
                </div>
              </div>
            </template>
            <template v-if="!permissionsLoading && permissionsData.length == 0">
              <NoData :type="'teams'" />
            </template>
          </div>
          
          <div class="permissions__buttons" v-if="showButtons && !buttonsLoading && can('permission_access') && can('permission_edit')">
            <button
              class="button button--default button--shadow"
              @click="applyPermissions()"
            >
              {{ $t(`buttons.${"saveBtn"}`) }}
            </button>
            <button class="button button--cancel" @click="resetPermissions()">
              {{ $t(`buttons.${"cancelBtn"}`) }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import NoData from "@/partials/NoData";
import Loading from "@/partials/Loading";

const store = useStore();
const route = useRoute();
const router = useRouter();

// Template refs
const permissions = ref(null);

// Reactive data
const rolesLoading = ref(false);
const permissionsLoading = ref(false);
const permissionIds = ref([]);
const permissionIdsCopy = ref([]);
const showButtons = ref(false);
const activeTeamId = ref(null);
const buttonsLoading = ref(false);

// Computed properties
const roles = computed(() => store.getters.roles);
const selectedRole = computed(() => store.getters.selectedRole);
const teams = computed(() => store.getters.teams);
const isSuperAdmin = computed(() => store.getters.isSuperAdmin);
const permissionsData = computed(() => store.getters.permissions);
const roleCollectionError = computed(() => store.getters.roleCollectionError);
const roleTeams = computed(() => {
  const authorizedIds = store.getters.authorizedTeamIds("role_access");

  if (authorizedIds === null) {
    return isSuperAdmin.value ? (teams.value || []) : [];
  }

  const allowedIds = new Set(Array.isArray(authorizedIds) ? authorizedIds : []);

  return store.getters.authorizationMemberships
    .filter((membership) => membership?.team?.id != null && allowedIds.has(membership.team.id))
    .map((membership) => membership.team);
});
const can = (ability) => {
  if (isSuperAdmin.value) return true;
  return activeTeamId.value !== null
    && store.getters.hasTeamPermission(ability, activeTeamId.value);
};

// Methods
const onTeamSelected = async (teamId) => {
  const normalizedTeamId = Number(teamId);
  activeTeamId.value = Number.isInteger(normalizedTeamId) && normalizedTeamId > 0 ? normalizedTeamId : null;
  permissionIds.value = [];
  permissionIdsCopy.value = [];
  showButtons.value = false;
  if (activeTeamId.value === null) return;
  if (String(route.query.team_id || "") !== String(activeTeamId.value)) {
    await router.replace({ query: { ...route.query, team_id: activeTeamId.value } });
  }
  try {
    await store.dispatch("getRolesByTeam", activeTeamId.value);
    if (can("permission_access")) await getAllPermissions();
    const [firstRole] = roles.value;
    if (firstRole) {
      store.commit("setSelectedRole", firstRole);
      loadPermissionIds();
    }
  } catch (err) {
    console.error(err);
  }
};

const getAllPermissions = async () => {
  permissionsLoading.value = true;
  await store.dispatch("getPermissions", activeTeamId.value);
  permissionsLoading.value = false;
};

const togglePermissions = (event, permissionId) => {
  const isChecked = event?.target?.checked ?? false;

  if (isChecked) {
    permissionIds.value = permissionIds.value.includes(permissionId)
      ? [...permissionIds.value]
      : [...permissionIds.value, permissionId];
  } else {
    permissionIds.value = permissionIds.value.filter((id) => id !== permissionId);
  }
};

const selectRole = (roleItem) => {
  store.commit("setSelectedRole", roleItem);
  loadPermissionIds();
  permissions.value.scrollTo(0, 0);
};

const deleteReportModal = (id) => {
  store.commit("setDeleteModal", {
    display: true,
    message: '',
    action: () => deleteRole(id),
    id: id,
  });
};

const deleteRole = async (id) => {
  try {
    await store.dispatch("deleteRole", { id, teamId: activeTeamId.value });
    onTeamSelected(activeTeamId.value);
  } catch (err) {
    console.error(err);
  }
};

const resetPermissions = () => {
  loadPermissionIds();
};

const loadPermissionIds = () => {
  permissionIds.value = (selectedRole.value.permissions || []).map(
    (permission) => permission.id
  );
  permissionIdsCopy.value = [...permissionIds.value];
};

const applyPermissions = async () => {
  permissionsLoading.value = true;
  buttonsLoading.value = true;
  try {
    const { data } = await store.dispatch("updateRole", {
      id: selectedRole.value.id,
      permissions: permissionIds.value,
      teamId: activeTeamId.value,
    });

    try {
      await store.dispatch("getRolesByTeam", activeTeamId.value);
      if (can("permission_access")) await getAllPermissions();
      const { data: roleData } = data;
      if (roleData) {
        store.commit("setSelectedRole", roleData);
        loadPermissionIds();
      }
    } catch (err) {
      console.error(err);
    }

    await store.dispatch(
      "updateLoggedUser",
      store.getters.loggedUser.id
    );
  } catch (err) {
    console.error(err);
  } finally {
    buttonsLoading.value = false;
  permissionsLoading.value = false;
  }
};

// Watchers
watch(permissionIds, (permissionsValue) => {
  showButtons.value =
    permissionIdsCopy.value.some(
      (permission) => !permissionsValue.includes(permission)
    ) || permissionsValue.length > permissionIdsCopy.value.length;
});

watch(roleTeams, (newTeams) => {
  if (newTeams.length === 1) {
    activeTeamId.value = newTeams[0].id;
    onTeamSelected(activeTeamId.value);
  }
});

// Lifecycle
onMounted(async () => {
  store.commit("setRoles", []);
  store.commit("setPermissions", []);

  if (isSuperAdmin.value && !teams.value.length) {
    rolesLoading.value = true;
    await store.dispatch("getTeams");
    rolesLoading.value = false;
  }

  const requestedTeamId = Number(route.query.team_id);
  const initialTeam = roleTeams.value.find((team) => team.id === requestedTeamId)
    || roleTeams.value.find((team) => team.id === store.getters.currentTeamId)
    || roleTeams.value[0];
  if (initialTeam) {
    await onTeamSelected(initialTeam.id);
  }
});
</script>

<style scoped lang="scss">
.user__select {
    padding: 0 20px  ;
    margin-bottom: 22px;
}

.delete-role-btn-wrapper{
  justify-content: flex-end
}
.delete-role-btn{
  margin-top: 20px;
  margin-right: 20px;
}

.rights {
  height: 100%;

  .page__header {
    .buttons {
      width: 180px;
      position: relative;
      cursor: pointer;

      .button {
        width: 100%;
      }

      .dropdown {
        position: absolute;
        width: 100%;
        display: none;
        padding: 5px 0;
        &__container {
          position: absolute;
          z-index: 3;
          width: 100%;
          background: var(--bg-2);
          border-radius: var(--radius-1);
          padding: 10px 0;
          top: calc(100% - 5px);

          &::after {
            content: "";
            position: absolute;
            top: -4px;
            left: 50%;
            transform: translateX(-50%);
            border-left: 5px solid transparent;
            border-right: 5px solid transparent;
            border-bottom: 4px solid var(--border-2);
          }
        }

        &__item {
          padding: 10px 12px;
          color: var(--text-1);
          font-size: var(--medium);
          font-family: var(--muli-text-semi-bold);

          &:hover {
            background: var(--bg-19);
          }
        }
      }

      &:hover {
        .button {
          background-color: var(--hover-1);
          box-shadow: 0px -7px 18px var(--shadow-2);
        }
        .dropdown {
          display: block;
        }
      }
    }
  }

  &__container {
    height: calc(100% - 48px - 20px);
    display: flex;
  }

  .roles,
  .permissions {
    height: 100%;
    background: var(--bg-1);
    position: relative;
  }

  .roles__role,
  .permissions__permission {
    color: var(--text-9);
    font-size: var(--medium);
    padding: 10px 20px;
    cursor: pointer;

    &:hover,
    &--selected {
      background: var(--bg-17);
    } 

    
  }

  .permission-msg{
    cursor: initial;
    &:hover,
    &--selected {
      background: transparent;
    } 
  }

  .roles,
  .permissions {
    &__body {
      margin-top: 65px;
      padding-top: 20px;
      overflow: auto;
    }
  }

  .roles {
    width: 400px;
    margin-right: 20px;

    &__body {
      height: calc(100% - 65px);
    }
  }

  .permissions {
    flex: 1;

    &__body {
      max-height: calc(100% - 65px - 88px);

      &--stretch {
        height: calc(100% - 65px);
      }
    }

    &__permission {
      border-bottom: 1px solid var(--border-15);
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 15px 20px;
    }

    &__buttons {
      display: flex;
      justify-content: center;
      padding: 20px 0;

      .button {
        height: 40px;
        width: 140px;

        &:first-child {
          margin-right: 80px;
        }
      }
    }
  }
}
</style>
